import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { ClipboardList, ChevronRight, Lock, Search, X, LayoutList, Brain, Route as JourneyIcon, Shapes } from "lucide-react";
import understandImage from "@/assets/understand-learning-hub.jpg";
import { useMembership } from "@/hooks/useMembership";
import { isGuestOpenAssessment, openGuestLock } from "@/lib/guest-access";
import { GuestBanner } from "@/components/guest/GuestBanner";
import {
  ALL_ASSESSMENTS,
  assessmentsByCategory,
  searchAssessments,
} from "@/data/learn/assessments";
import type { Assessment } from "@/data/learn/assessment-types";
import { getAssessmentTheme } from "@/lib/assessment-theme";

export const Route = createFileRoute("/learn/assessments/")({
  head: () => ({
    meta: [
      { title: "Self-Assessments — Recovery Trauma™" },
      {
        name: "description",
        content:
          "27 short educational self-assessments on trauma responses, emotions, relationships, thoughts and daily life. Browse by category, search or use the A–Z. Reflection tools, never a diagnosis.",
      },
      { property: "og:title", content: "Self-Assessments — Recovery Trauma™" },
      {
        property: "og:description",
        content:
          "Reflect on survival responses, emotional flashbacks, boundaries, sleep and more — then explore the Recovery Trauma™ resources that fit.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://recoverytrauma.com/learn/assessments" },
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/learn/assessments" }],
  }),
  component: AssessmentsIndex,
});

const TOP_DISCLAIMER =
  "These short self-assessments are designed to help you reflect on patterns and experiences you may recognise in yourself. They are educational and self-reflective tools only and cannot diagnose PTSD, C-PTSD or any other mental-health condition.";

export function AssessmentCard({
  assessment,
  index = 0,
  compact = false,
}: {
  assessment: Assessment;
  index?: number;
  compact?: boolean;
}) {
  const { themeClass, Icon } = getAssessmentTheme(assessment.slug, index);
  const { loading, isAuthed } = useMembership();
  const locked = !loading && !isAuthed && !isGuestOpenAssessment(assessment.slug);
  const cardClass = `assess-card ${themeClass} group flex h-full w-full flex-col text-left ${
    compact ? "p-3 sm:p-4" : "p-5 sm:p-6"
  }`;

  const body = (
    <>
      <span className="assess-card__glow" aria-hidden="true" />
      <span className="flex items-start justify-between gap-2">
        <span className="assess-card__icon assess-card__meta" aria-hidden="true">
          <Icon className={compact ? "h-3.5 w-3.5 sm:h-4 sm:w-4" : "h-5 w-5"} />
        </span>
        {locked ? (
          <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-border/70 bg-background/70 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-muted-foreground sm:px-2 sm:text-[10px]">
            <Lock className="h-2.5 w-2.5 sm:h-3 sm:w-3" aria-hidden="true" /> Members
          </span>
        ) : null}
      </span>
      <h3
        className={`mt-2.5 font-serif leading-snug text-foreground sm:mt-3 ${
          compact ? "text-sm sm:text-base" : "text-xl"
        }`}
      >
        <span className="me-1.5" aria-hidden="true">
          {assessment.emoji}
        </span>
        {assessment.title}
      </h3>
      <p
        className={`mt-1.5 leading-relaxed text-foreground/75 ${
          compact ? "text-[11px] sm:text-xs" : "text-sm"
        }`}
      >
        {assessment.tagline}
      </p>
      <span
        className={`assess-card__meta mt-auto inline-flex items-center gap-1 pt-2.5 font-semibold sm:pt-3 ${
          compact ? "text-[11px] sm:text-xs" : "text-sm"
        }`}
      >
        {assessment.questions.length} questions
        <ChevronRight className="h-3.5 w-3.5 transition group-hover:translate-x-0.5 sm:h-4 sm:w-4" />
      </span>
    </>
  );

  if (locked) {
    return (
      <button type="button" onClick={() => openGuestLock(assessment.title)} className={cardClass}>
        {body}
      </button>
    );
  }

  return (
    <Link to="/learn/assessments/$slug" params={{ slug: assessment.slug }} className={cardClass}>
      {body}
    </Link>
  );
}

/** Horizontal, swipeable rail — the mobile-first alternative to a long list. */
export function AssessmentRail({ items, label }: { items: Assessment[]; label: string }) {
  return (
    <ul
      aria-label={label}
      className="-mx-4 flex snap-x snap-mandatory gap-3 overflow-x-auto px-4 pb-2 [-webkit-overflow-scrolling:touch] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
    >
      {items.map((a, i) => (
        <li key={a.slug} className="w-[74%] shrink-0 snap-start sm:w-[46%] lg:w-[31%]">
          <AssessmentCard assessment={a} index={i} compact />
        </li>
      ))}
    </ul>
  );
}

function AssessmentsIndex() {
  const { loading, isAuthed } = useMembership();
  const showGuestBanner = !loading && !isAuthed;
  const [query, setQuery] = useState("");
  const results = useMemo(() => searchAssessments(query), [query]);
  const groups = useMemo(() => assessmentsByCategory(results), [results]);
  const searching = query.trim().length > 0;

  return (
    <main className="mx-auto max-w-5xl px-4 py-8 sm:py-10">
      <nav className="mb-5 text-sm text-muted-foreground">
        <Link to="/learn" className="hover:text-foreground">
          Learn
        </Link>{" "}
        / Self-Assessments
      </nav>

      {showGuestBanner ? <GuestBanner /> : null}

      <header className="mb-6">
        <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs text-muted-foreground">
          <ClipboardList className="h-3.5 w-3.5" /> {ALL_ASSESSMENTS.length} self-assessments
        </div>
        <h1 className="font-serif text-3xl tracking-tight text-foreground md:text-4xl">
          Self-Assessments
        </h1>
        <p className="mt-3 max-w-2xl rounded-2xl border border-border bg-muted/40 p-4 text-sm leading-relaxed text-muted-foreground">
          {TOP_DISCLAIMER}
        </p>
      </header>

      {!searching && (
        <section aria-labelledby="deeper-understanding" className="mb-9">
          <div className="relative mb-4 overflow-hidden rounded-2xl border border-border">
            <img src={understandImage} alt="An open notebook in a calm library setting" width={1536} height={768} className="h-36 w-full object-cover sm:h-48" />
            <div className="absolute inset-0 bg-gradient-to-t from-background/95 via-background/35 to-transparent" aria-hidden="true" />
            <div className="absolute inset-x-0 bottom-0 p-4 sm:p-5">
              <h2 id="deeper-understanding" className="font-serif text-xl text-foreground sm:text-2xl">Go deeper into the psychology of trauma</h2>
              <p className="mt-1 max-w-2xl text-xs leading-relaxed text-foreground/75 sm:text-sm">Explore where today’s ideas came from and what a non-linear recovery journey can involve.</p>
            </div>
          </div>
          <div className="-mx-4 flex snap-x snap-mandatory gap-3 overflow-x-auto px-4 pb-2 [touch-action:pan-x] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:mx-0 sm:grid sm:grid-cols-3 sm:overflow-visible sm:px-0">
            <Link to="/learn/assessments/pioneers" className="group flex min-h-[190px] w-[82%] shrink-0 snap-start flex-col rounded-2xl border border-sky-500/25 bg-sky-500/[0.07] p-5 sm:w-auto">
              <Brain className="h-7 w-7 text-sky-700 dark:text-sky-300" aria-hidden="true" />
              <h3 className="mt-4 font-serif text-xl text-foreground">Psychology & Trauma Pioneers</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">Meet 19 thinkers and see which ideas are supported, influential, historical or debated.</p>
              <span className="mt-auto inline-flex items-center gap-1 pt-4 text-sm font-semibold text-[var(--gold-deep)]">Explore the profiles <ChevronRight className="h-4 w-4 transition group-hover:translate-x-0.5" /></span>
            </Link>
            <Link to="/learn/assessments/trauma-healing-journey" className="group flex min-h-[190px] w-[82%] shrink-0 snap-start flex-col rounded-2xl border border-emerald-500/25 bg-emerald-500/[0.07] p-5 sm:w-auto">
              <JourneyIcon className="h-7 w-7 text-emerald-700 dark:text-emerald-300" aria-hidden="true" />
              <h3 className="mt-4 font-serif text-xl text-foreground">The Trauma Healing Journey</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">Explore safety, regulation, processing, relationships and integration without a fixed order.</p>
              <span className="mt-auto inline-flex items-center gap-1 pt-4 text-sm font-semibold text-[var(--gold-deep)]">Explore the journey <ChevronRight className="h-4 w-4 transition group-hover:translate-x-0.5" /></span>
            </Link>
            <Link to="/learn/personality" className="group flex min-h-[190px] w-[82%] shrink-0 snap-start flex-col rounded-2xl border border-amber-500/25 bg-amber-500/[0.07] p-5 sm:w-auto">
              <Shapes className="h-7 w-7 text-amber-700 dark:text-amber-300" aria-hidden="true" />
              <h3 className="mt-4 font-serif text-xl text-foreground">Understanding Personality &amp; Personality Disorders</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">Explore traits, all ten disorders, trauma overlap, relationships and treatment without self-diagnosis.</p>
              <span className="mt-auto inline-flex items-center gap-1 pt-4 text-sm font-semibold text-[var(--gold-deep)]">Explore the library <ChevronRight className="h-4 w-4 transition group-hover:translate-x-0.5" /></span>
            </Link>
          </div>
        </section>
      )}

      {/* Search */}
      <section aria-label="Search self-assessments" className="mb-8">
        <div className="relative">
          <Search
            className="pointer-events-none absolute start-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search self-assessments — try “freeze”, “sleep”, “abandonment”…"
            aria-label="Search self-assessments"
            className="w-full rounded-full border border-border bg-card py-3 ps-11 pe-11 text-sm text-foreground shadow-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
          {searching && (
            <button
              type="button"
              onClick={() => setQuery("")}
              aria-label="Clear search"
              className="absolute end-3 top-1/2 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        {searching && (
          <p className="mt-2 px-1 text-xs text-muted-foreground">
            {results.length} {results.length === 1 ? "assessment" : "assessments"} match “{query}”
          </p>
        )}
      </section>

      {!searching && (
        <div className="mb-10">
          <Link
            to="/learn/assessments/a-z"
            className="inline-flex min-h-11 items-center gap-2 rounded-full border border-border bg-card px-5 py-2.5 text-sm font-semibold text-foreground transition hover:border-gold hover:text-gold"
          >
            <LayoutList className="h-4 w-4" /> Browse Self-Assessments A–Z
          </Link>
        </div>
      )}

      {/* Categories */}
      <section aria-labelledby="browse-category">
        <h2 id="browse-category" className="mb-4 font-serif text-xl text-foreground">
          {searching ? "Matching self-assessments" : "Browse by category"}
        </h2>

        {groups.length === 0 ? (
          <p className="rounded-2xl border border-border bg-card p-6 text-center text-sm text-muted-foreground">
            Nothing matched “{query}”. Try a word like “freeze”, “relationship”, “sleep” or
            “overthinking”.
          </p>
        ) : (
          <div className="space-y-10">
            {groups.map((g) => (
              <div key={g.category.key}>
                <div className="mb-3">
                  <h3 className="font-serif text-lg text-foreground">
                    <span className="me-1.5" aria-hidden="true">
                      {g.category.emoji}
                    </span>
                    {g.category.label}
                  </h3>
                  <p className="mt-0.5 text-sm text-muted-foreground">{g.category.blurb}</p>
                </div>

                {/* Mobile: swipeable rail. Desktop: compact grid. */}
                <div className="sm:hidden">
                  <AssessmentRail items={g.items} label={g.category.label} />
                </div>
                <ul className="hidden gap-4 sm:grid sm:grid-cols-2 lg:grid-cols-3">
                  {g.items.map((a, i) => (
                    <li key={a.slug}>
                      <AssessmentCard assessment={a} index={i} />
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </section>

      {searching && (
        <div className="mt-10">
          <Link
            to="/learn/assessments/a-z"
            className="inline-flex min-h-11 items-center gap-2 rounded-full border border-border bg-card px-5 py-2.5 text-sm font-semibold text-foreground transition hover:border-gold hover:text-gold"
          >
            <LayoutList className="h-4 w-4" /> Browse Self-Assessments A–Z
          </Link>
        </div>
      )}
    </main>
  );
}
